import React, { useEffect, useState } from 'react'
import { gql, useMutation, useQuery } from '@apollo/client'
import {
  Box, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, InputAdornment, TextField, Typography,
} from '@mui/material'
import { Icon, T } from '../PartnerSignups/onboardingDesign'
import { Badge, FieldLabel, GhostButton, LinkButton, Notice, PrimaryButton } from '../PartnerSignups/onboardingUi'

/**
 * Super admin: pick restaurants for one agent, ISO or ISO agent, with check
 * boxes and a search, and credit them all from one day (backend
 * utils/restaurantCredit.js assignMany). Each is assigned on its own: one
 * that can't move (a settled month) is reported and the rest go through.
 * Restaurants already theirs are shown checked and can't be picked; taking
 * one away is Unassign in their restaurants list.
 */

const CREDIT_FIELDS = 'restaurantId restaurantName address kind vendorId name isoName since minStart'
const SEARCH = gql`query CreditRestaurantsPicker($search: String) { creditRestaurants(search: $search) { ${CREDIT_FIELDS} } }`
const ASSIGNEES = gql`query CreditAssigneesPicker { creditAssignees { vendorId kind name isoName terms minStart } creditToday }`
const ASSIGN_MANY = gql`
  mutation AssignRestaurantsCredit($restaurantIds: [ID!]!, $vendorId: ID!, $startsOn: String!) {
    assignRestaurantsCredit(restaurantIds: $restaurantIds, vendorId: $vendorId, startsOn: $startsOn) { restaurantId restaurantName ok error }
  }
`

const dayText = ymd => (ymd ? new Date(`${ymd}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '')
const who = c => (c.kind === 'ISO_AGENT' ? `${c.name} (${c.isoName})` : c.name)
const latest = (...days) => days.filter(Boolean).sort().pop() || null
const cap = t => (t ? t.charAt(0).toUpperCase() + t.slice(1) : t)
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`

export default function AssignRestaurantsDialog({ vendorId, name, onClose, onDone, onChanged }) {
  const [text, setText] = useState('')
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState({})
  const [startsOn, setStartsOn] = useState('')
  const [failed, setFailed] = useState([])
  const [error, setError] = useState(null)
  const { data: a } = useQuery(ASSIGNEES, { fetchPolicy: 'network-only' })
  const { data: s, loading: searching, refetch } = useQuery(SEARCH, { variables: { search: search || null }, fetchPolicy: 'network-only' })
  const [assignMany, { loading: assigning }] = useMutation(ASSIGN_MANY)
  const today = a && a.creditToday
  const me = ((a && a.creditAssignees) || []).find(x => String(x.vendorId) === String(vendorId)) || null
  const rows = (s && s.creditRestaurants) || []
  const theirs = r => !!r.kind && String(r.vendorId) === String(vendorId)
  const pickable = rows.filter(r => !theirs(r))
  const picked = Object.values(selected)
  const moving = picked.filter(r => r.kind)
  const minStart = latest(me && me.minStart, ...moving.map(r => r.minStart))
  const allShown = pickable.length > 0 && pickable.every(r => selected[r.restaurantId])

  // Search as they type, a moment after they stop.
  useEffect(() => {
    const t = setTimeout(() => setSearch(text.trim()), 300)
    return () => clearTimeout(t)
  }, [text])
  useEffect(() => { if (today && !startsOn) setStartsOn(today) }, [today, startsOn])
  // A date before what's allowed moves up to the first allowed day.
  useEffect(() => { if (minStart && startsOn && startsOn < minStart) setStartsOn(minStart) }, [minStart, startsOn])

  const toggle = r => setSelected(cur => {
    const next = { ...cur }
    if (next[r.restaurantId]) delete next[r.restaurantId]
    else next[r.restaurantId] = r
    return next
  })
  const toggleAll = () => setSelected(cur => {
    const next = { ...cur }
    for (const r of pickable) {
      if (allShown) delete next[r.restaurantId]
      else next[r.restaurantId] = r
    }
    return next
  })

  const submit = async () => {
    setError(null)
    setFailed([])
    try {
      const res = await assignMany({ variables: { restaurantIds: picked.map(r => r.restaurantId), vendorId, startsOn } })
      const results = res.data.assignRestaurantsCredit || []
      const done = results.filter(r => r.ok)
      const notDone = results.filter(r => !r.ok)
      if (!notDone.length) {
        onDone(`${plural(done.length, 'restaurant')} assigned to ${name} from ${dayText(startsOn)}.`)
        return
      }
      // Keep the ones that didn't go through picked, and say why.
      setSelected(cur => Object.fromEntries(Object.entries(cur).filter(([id]) => notDone.some(r => String(r.restaurantId) === String(id)))))
      setFailed(notDone)
      await refetch()
      if (done.length && onChanged) onChanged()
    } catch (e) {
      setError(e.message)
    }
  }

  return (
    <Dialog open onClose={assigning ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Assign restaurants to {name}
        {me && <Typography sx={{ color: T.muted, fontSize: 13.5, mt: 0.25 }}>{cap(me.terms)}</Typography>}
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: T.divider }}>
        {error && <Notice severity="error" onClose={() => setError(null)}>{error}</Notice>}
        {failed.length > 0 && (
          <Notice severity="warning" onClose={() => setFailed([])}>
            {failed.length === 1 ? '1 restaurant wasn’t assigned:' : `${failed.length} restaurants weren’t assigned:`}
            {failed.map(f => <Box key={f.restaurantId} component="span" sx={{ display: 'block' }}>{f.restaurantName || `#${f.restaurantId}`}: {f.error}</Box>)}
          </Notice>
        )}

        <TextField
          fullWidth size="small" autoFocus placeholder="Search restaurants by name or ID"
          value={text} onChange={e => setText(e.target.value)}
          inputProps={{ 'aria-label': 'Search restaurants' }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Icon.Search size={16} /></InputAdornment>,
            endAdornment: searching ? <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment> : null,
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 1, flexWrap: 'wrap' }}>
          <FormControlLabel
            control={<Checkbox size="small" checked={allShown} indeterminate={!allShown && pickable.some(r => selected[r.restaurantId])} onChange={toggleAll} disabled={!pickable.length} />}
            label={<Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>Select all shown</Typography>}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: 13.5, color: picked.length ? T.text : T.hint, fontWeight: picked.length ? 700 : 400 }}>{picked.length} selected</Typography>
            {picked.length > 0 && <LinkButton size="small" onClick={() => setSelected({})} sx={{ color: T.muted }}>Clear</LinkButton>}
          </Box>
        </Box>
        <Box sx={{ border: `1px solid ${T.cardBorder}`, borderRadius: '12px', overflow: 'hidden', maxHeight: 340, overflowY: 'auto', mb: 2.5 }}>
          {rows.length === 0 ? (
            <Typography sx={{ p: 3, textAlign: 'center', color: T.hint, fontSize: 13.5 }}>{searching ? 'Loading…' : search ? `No restaurant matches “${search}”.` : 'No restaurants.'}</Typography>
          ) : rows.map(r => {
            const mine = theirs(r)
            return (
              <Box
                key={r.restaurantId} component="label"
                sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5, borderTop: `1px solid ${T.divider}`, '&:first-of-type': { borderTop: 0 }, cursor: mine ? 'default' : 'pointer', '&:hover': mine ? {} : { background: T.fill } }}
              >
                <Checkbox size="small" checked={mine || !!selected[r.restaurantId]} disabled={mine} onChange={() => toggle(r)} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{r.restaurantName}</Typography>
                  <Typography sx={{ color: T.hint, fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {[r.address, mine ? null : r.kind ? `credited to ${who(r)}` : 'not credited'].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
                {mine && <Badge kind="green" sx={{ flexShrink: 0, mr: 1 }}>Already theirs</Badge>}
              </Box>
            )
          })}
        </Box>

        <FieldLabel htmlFor="assign-many-start" label="Start date" />
        <TextField
          id="assign-many-start" type="date" size="small" value={startsOn} onChange={e => setStartsOn(e.target.value)}
          inputProps={{ min: minStart || undefined, max: today || undefined }}
          helperText={minStart
            ? `Orders from this day count. It can be in the past, from ${dayText(minStart)} on: earlier months are already settled.`
            : 'Orders from this day count. It can be in the past.'}
        />

        {picked.length > 0 && startsOn && (
          <Notice severity="info" sx={{ mt: 2.5, mb: 0 }}>
            {name} earns on {plural(picked.length, 'restaurant')} from {dayText(startsOn)}.
            {moving.length === 1 && ` ${picked.length === 1 ? 'It' : 'One of them'} moves from ${who(moving[0])}, who stops earning on it that day.`}
            {moving.length > 1 && ` ${moving.length} of them move from whoever has them now, who stops earning on them that day.`}
          </Notice>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap' }}>
        <GhostButton onClick={onClose} disabled={assigning}>Cancel</GhostButton>
        <PrimaryButton busy={assigning} disabled={assigning || !picked.length || !startsOn} onClick={submit}>
          Assign {picked.length ? plural(picked.length, 'restaurant') : ''}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  )
}
